import {
  createContext,
  DependencyList,
  ReactNode,
  useContext,
  useId,
  useLayoutEffect,
} from "react";

export interface PageIntroConfig {
  /** Replaces the default one-line description for the route. */
  description?: ReactNode;
  /** Page-level actions shown on the right of the title (buttons, toggles). */
  actions?: ReactNode;
  /** Content shown under the title row, e.g. page tabs. */
  footer?: ReactNode;
  /** Hides the intro for full-bleed pages (terminal, forms). */
  hidden?: boolean;
}

export interface OwnedPageIntroConfig {
  owner?: string;
  config: PageIntroConfig;
}

export const PageIntroContext = createContext<{
  config: PageIntroConfig;
  setConfig: (
    update: (current: OwnedPageIntroConfig) => OwnedPageIntroConfig
  ) => void;
}>({ config: {}, setConfig: () => {} });

const EMPTY: OwnedPageIntroConfig = { config: {} };

/**
 * Lets a page customize the new-look page intro (title row) rendered by the
 * page wrapper. The last caller wins, and unmounting only clears the config it
 * set, so a component that unmounts can't wipe another page's intro. Pass
 * `deps` for the values the config depends on, like `useMemo`.
 */
export const usePageIntro = (config: PageIntroConfig, deps: DependencyList) => {
  const { setConfig } = useContext(PageIntroContext);
  const owner = useId();
  useLayoutEffect(() => {
    setConfig(() => ({ owner, config }));
    return () =>
      setConfig((current) => (current.owner === owner ? EMPTY : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConfig, owner, ...deps]);
};
